// Real HTTP gateway (VITE_USE_MOCKS=false). Both axios instances use the
// Firebase `/api/**` rewrite. withCredentials:false everywhere
// (Bearer header, zero cookies).
// COG/blob reads are NOT routed here (MapLibre reads signed URLs directly).
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from "axios";
import type { OrgRole } from "../types/contract";
import { ApiError, isWireEnvelope, unwrap, type WireEnvelope } from "./unwrap";
import type { CallScope, Gateway } from "./gateway";
import { orgGet, withEnvelope } from "./withEnvelope";
import { config } from "../config";
import { getAccessToken, setAccessToken } from "../lib/authToken";
import { getActiveOrg } from "../lib/activeOrg";
import { firebaseSessionActive } from "../lib/firebaseSession";
import { refreshFirebaseIdToken } from "../lib/firebaseAuth";
import { attachRefreshHost, runRefresh } from "./refreshFlight";
import {
  firebaseRetryEligible,
  requestStillCurrent,
  shouldDropSession,
  stampRequest,
  takeFreshIdToken,
  type StampedConfig,
} from "./sessionGate";

const STORAGE_LOCK_KEY = "sm_refresh_lock";
export const LOGOUT_TIMEOUT_MS = 4_000;
// The reset-complete route answers after its 1 s floor plus at most a 5 s
// Firebase read. A hung API must not hold the "Save password" screen.
export const RESET_COMPLETE_TIMEOUT_MS = 15_000;
const SESSION_EXPIRED = "Your session expired. Sign in again.";
const SLOT_KEY = "__veroRefreshSlot";

export interface RefreshedSession {
  access_token: string;
  refresh_token?: string;
  user_id?: string;
  email?: string;
  orgs?: { id: string; role: OrgRole }[];
  role?: string;
}
export interface AccessMemory {
  get(): string | null;
  set(token: string | null): void;
}
type Slot = { access: string; refresh: string };
type Shared = { [SLOT_KEY]?: Slot };
type Retried = AxiosRequestConfig & { __retried?: boolean };
type Flight = {
  generation: number;
  inflight: Promise<RefreshedSession> | null;
  memory: AccessMemory;
};

const moduleMemory: AccessMemory = {
  get: getAccessToken,
  set: setAccessToken,
};
let onSessionLost: (message: string) => void = () => {};
let onUnavailable: (message: string) => void = () => {};

type RefreshEngine = {
  installRefreshListener: () => void;
  lockRefresh: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
  askPeerAccess: (refresh: string) => Promise<string | null>;
  publishRefresh: (access: string, refresh: string) => void;
};
let enginePromise: Promise<RefreshEngine> | null = null;
function engine(): Promise<RefreshEngine> {
  if (!enginePromise) {
    enginePromise = import("../stores/authStore").then((mod) => ({
      installRefreshListener: mod.installRefreshListener,
      lockRefresh: mod.lockRefresh,
      askPeerAccess: mod.askPeerAccess,
      publishRefresh: mod.publishRefresh,
    }));
  }
  return enginePromise;
}

export function setSessionLostHandler(fn: (message: string) => void): void {
  onSessionLost = fn;
}
export function setUnavailableHandler(fn: (message: string) => void): void {
  onUnavailable = fn;
}
function clearSlot(): void {
  delete (globalThis as Shared)[SLOT_KEY];
}
function dropSession(state: { generation: number }): void {
  state.generation += 1;
  clearSlot();
  onSessionLost(SESSION_EXPIRED);
}
attachRefreshHost({
  dropSession,
  unavailable: (message) => onUnavailable(message),
  engine,
});

export interface RefreshController {
  refreshShared: () => Promise<RefreshedSession>;
  abandon: () => void;
  generation: () => number;
  reset: () => void;
  lose: () => void;
  memory: AccessMemory;
}
const logoutPeers = new Set<() => void>();
export function fanoutLogout(): void {
  for (const fn of logoutPeers) fn();
}
export function createRefreshController(
  memory: AccessMemory = moduleMemory,
): RefreshController {
  const state: Flight = { generation: 0, inflight: null, memory };
  const bump = () => {
    state.generation += 1;
    clearSlot();
  };
  logoutPeers.add(bump);
  return {
    memory,
    generation: () => state.generation,
    abandon: bump,
    lose: () => dropSession(state),
    reset() {
      bump();
      state.inflight = null;
      try {
        localStorage.removeItem(STORAGE_LOCK_KEY);
      } catch {
        return;
      }
    },
    refreshShared() {
      if (!state.inflight) {
        const gen = state.generation;
        state.inflight = runRefresh(state, gen).finally(() => {
          state.inflight = null;
        });
      }
      return state.inflight;
    },
  };
}

const appRefresh = createRefreshController();
export function refreshShared(): Promise<RefreshedSession> {
  return appRefresh.refreshShared();
}
export function abandonInFlightRefresh(): void {
  appRefresh.abandon();
}
export function refreshGeneration(): number {
  return appRefresh.generation();
}
export function resetRefreshFlightForTests(): void {
  appRefresh.reset();
}

async function replay(
  inst: AxiosInstance,
  cfg: Retried,
  controller: RefreshController,
): Promise<unknown> {
  try {
    return await inst(cfg);
  } catch (replayErr) {
    const failed = axios.isAxiosError(replayErr) ? replayErr : null;
    if (
      failed?.response?.status === 401 &&
      requestStillCurrent(
        failed.config as StampedConfig | undefined,
        controller.generation(),
      )
    )
      controller.lose();
    return Promise.reject(replayErr);
  }
}
function orgHeaders(
  scope: CallScope | undefined,
): Record<string, string> | undefined {
  if (!scope?.activeOrg) return undefined;
  const org = getActiveOrg();
  // A missing header is the union of every org on the token.
  if (!org) throw new ApiError("NO_ACTIVE_ORG", "Choose an organization.");
  return { "X-Active-Org": org };
}

async function firebaseReplay(
  inst: AxiosInstance,
  err: AxiosError,
  controller: RefreshController,
): Promise<unknown | undefined> {
  const cfg = err.config as StampedConfig | undefined;
  if (
    !firebaseRetryEligible(
      cfg,
      err.response?.status,
      controller.generation(),
      firebaseSessionActive(),
    )
  )
    return undefined;
  // A Firebase session has no refresh token. A 401 asks the SDK for a new ID token.
  cfg.__retried = true;
  const token = await takeFreshIdToken(
    cfg.__veroGen,
    () => controller.generation(),
    () => controller.lose(),
    refreshFirebaseIdToken,
  );
  if (!token) return Promise.reject(err);
  controller.memory.set(token);
  return replay(inst, cfg as Retried, controller);
}

async function recover(
  inst: AxiosInstance,
  err: AxiosError,
  controller: RefreshController,
): Promise<unknown> {
  const renewed = await firebaseReplay(inst, err, controller);
  if (renewed !== undefined) return renewed;
  // A password session cannot be refreshed. A live 401 signs that browser out.
  // A 401 from a call that had no bearer, or from a generation logout already
  // bumped, is not this session ending.
  const cfg = err.config as StampedConfig | undefined;
  if (shouldDropSession(cfg, err.response?.status, controller.generation()))
    controller.lose();
  return Promise.reject(err);
}
function makeInstance(
  baseURL: string,
  controller: RefreshController,
): AxiosInstance {
  const inst = axios.create({ baseURL, withCredentials: false });
  inst.interceptors.request.use((cfg) => stampRequest(cfg, controller));
  // One promise per controller, shared by the API and compute clients.
  // Auth URLs never enter this path (shouldAttemptRefresh).
  inst.interceptors.response.use(
    (r) => r,
    (err: AxiosError) => recover(inst, err, controller),
  );
  return inst;
}
function isLogout(path: string): boolean {
  return /(^|\/)auth\/logout$/.test(path);
}
function postTimeout(path: string): number | undefined {
  if (isLogout(path)) return LOGOUT_TIMEOUT_MS;
  if (/(^|\/)auth\/password-reset\/complete$/.test(path))
    return RESET_COMPLETE_TIMEOUT_MS;
  return undefined;
}

export function createHttpGateway(
  controller: RefreshController = appRefresh,
): Gateway {
  const api = makeInstance(config.apiBaseUrl, controller);
  const compute = makeInstance(config.apiBaseUrl, controller);
  const bareCompute = axios.create({
    baseURL: config.apiBaseUrl,
    withCredentials: false,
  });
  return {
    async apiGet(path, params = {}, scope, init) {
      return orgGet(api, path, params, orgHeaders(scope), init);
    },
    async apiPost(path, body, scope) {
      const timeout = postTimeout(path);
      return withEnvelope(
        api.post(path, body, {
          headers: orgHeaders(scope),
          ...(timeout ? { timeout } : {}),
        }),
      );
    },
    async apiPostBinary(
      path,
      bytes,
      params = {},
      scope,
      contentType = "application/zip",
    ) {
      // A copied ArrayBuffer, not the Uint8Array view: axios posts a view's whole backing
      // store. A Blob is the same bytes in a browser, but jsdom's XHR turns a Blob into the
      // string "[object Blob]", so the zip magic never reaches the request.
      const body = bytes.buffer.slice(
        bytes.byteOffset,
        bytes.byteOffset + bytes.byteLength,
      );
      return withEnvelope(
        api.post(path, body, {
          params,
          headers: {
            "Content-Type": contentType,
            ...orgHeaders(scope),
          },
        }),
      );
    },
    async apiPut(path, body, scope) {
      return withEnvelope(api.put(path, body, { headers: orgHeaders(scope) }));
    },
    async apiPatch(path, body, scope) {
      return withEnvelope(
        api.patch(path, body, { headers: orgHeaders(scope) }),
      );
    },
    async apiDelete(path, scope) {
      return withEnvelope(api.delete(path, { headers: orgHeaders(scope) }));
    },
    async computePost(path, body, scope) {
      return withEnvelope(
        compute.post(path, body, { headers: orgHeaders(scope) }),
      );
    },
    async computeGet(path) {
      // That is also the honest contract: this endpoint is documented GLOBAL and UNAUTHENTICATED
      // so sending a bearer token to it was never right. Filed for the underlying CORS gap:
      // `Back-End-Async__cors-rejects-GET-preflight`.
      return withEnvelope(bareCompute.get(path));
    },
    async uploadToSignedUrl(signedUrl, file, contentType) {
      // Straight to GCS — the signed URL is the capability; no bearer, no withCredentials.
      // The Content-Type header MUST match the minted content_type or GCS 403s
      // (SignatureDoesNotMatch). And fetch only rejects on network errors, so check status.
      const res = await fetch(signedUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (!res.ok)
        throw new Error(`Upload failed (${res.status}). Please try again.`);
    },
  };
}

// JSON posts go through the same client as the rest of the session, so an
// expired access token is refreshed and the call is retried.
export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const api = makeInstance(config.apiBaseUrl, appRefresh);
  try {
    const res = await api.post(path, body);
    if (isWireEnvelope(res.data)) return unwrap(res.data as WireEnvelope<T>);
    throw new Error("response was not an envelope");
  } catch (error) {
    if (axios.isAxiosError(error))
      throw new Error(await fileError(error), { cause: error });
    throw error;
  }
}

// File downloads go through the same client as the rest of the session, so an
// expired access token is refreshed and the download is retried.
export async function postJsonForFile(
  path: string,
  body: unknown,
): Promise<{ blob: Blob; disposition: string | null }> {
  const api = makeInstance(config.apiBaseUrl, appRefresh);
  try {
    const res = await api.post<Blob>(path, body, { responseType: "blob" });
    const data: unknown = res.data;
    return {
      blob: data instanceof Blob ? data : new Blob([data as BlobPart]),
      disposition: dispositionOf(res.headers),
    };
  } catch (error) {
    if (axios.isAxiosError(error))
      throw new Error(await fileError(error), { cause: error });
    throw error;
  }
}

function dispositionOf(headers: unknown): string | null {
  if (!headers || typeof headers !== "object") return null;
  const readable = headers as {
    get?: (name: string) => unknown;
    "content-disposition"?: unknown;
  };
  const value =
    readable.get?.("content-disposition") ?? readable["content-disposition"];
  return typeof value === "string" ? value : null;
}

async function fileError(error: AxiosError): Promise<string> {
  const message = envelopeMessage(await readJson(error.response?.data));
  if (message) return message;
  return `download failed (${error.response?.status ?? "network"})`;
}

function envelopeMessage(body: unknown): string | undefined {
  const reason = (
    body as { error?: { message?: string } | { message?: string }[] } | null
  )?.error;
  return Array.isArray(reason) ? reason[0]?.message : reason?.message;
}

async function readJson(data: unknown): Promise<unknown> {
  const text = await readBody(data);
  if (text == null) return data;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function readBody(data: unknown): Promise<string | null> {
  if (typeof Blob !== "undefined" && data instanceof Blob)
    return readBlob(data);
  if (typeof data === "string") return data;
  return null;
}

function readBlob(blob: Blob): Promise<string> {
  // jsdom's Blob has no text(). FileReader works there and in the browser.
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () =>
      reject(reader.error ?? new Error("could not read the download error"));
    reader.readAsText(blob);
  });
}
